/** Renaming items by a rule, as a file manager does (pages/items/rename.tsx asks for it). */
import type { ItemRow } from "../api/types.gen.ts";

/** A counter's digits ("00001"). */
const COUNTER_DIGITS = 5;
/** A name and its extension; an extension has a letter. */
const EXTENSION = /^(.+)(\.[A-Za-z0-9]{1,10})$/;
const LETTER = /[A-Za-z]/;

export type Mode = "replace" | "add" | "format";
export type Where = "before" | "after";
export type Format = "index" | "counter" | "date";

export interface Rule {
  mode: Mode;
  find: string;
  replace: string;
  text: string;
  where: Where;
  format: Format;
  name: string;
  start: number;
}

/** A file's name and its extension (`["photo", ".png"]`), as the server tells one: letters and digits, a letter
 *  among them, after the last dot. Anything else is all name. */
export function splitName(title: string, file: boolean): [string, string] {
  const m = file ? EXTENSION.exec(title) : null;
  return m?.[1] && m[2] && LETTER.test(m[2]) ? [m[1], m[2]] : [title, ""];
}

/** `row`'s new title by `rule`, the `i`th of those renamed. */
export function renamed(rule: Rule, row: Pick<ItemRow, "title" | "kind" | "created_at">, i: number): string {
  const [name, ext] = splitName(row.title ?? "", row.kind === "file");
  const put = (s: string, add: string) => (rule.where === "before" ? `${add}${s}` : `${s}${add}`);
  let next: string;
  if (rule.mode === "replace") {
    next = rule.find ? name.replaceAll(rule.find, rule.replace) : name;
  } else if (rule.mode === "add") {
    next = put(name, rule.text);
  } else {
    const n = rule.start + i;
    const token =
      rule.format === "index"
        ? String(n)
        : rule.format === "counter"
          ? String(n).padStart(COUNTER_DIGITS, "0")
          : row.created_at.slice(0, "YYYY-MM-DD".length);
    next = rule.where === "before" ? `${token} ${rule.name}` : `${rule.name} ${token}`;
  }
  return `${next.trim()}${ext}`;
}
